<template>
    <v-layout :full-height="!loaded" column>
        <hotel-booking-page-layout
            v-if="loaded && offerKeys"
            ref="layout"
            class="hotel-booking-page"
            :search-request="searchRequest"
            :room-offers="roomOffers"
            :offer-keys="offerKeys"
            :offer="offer"
            :search-request-country-id="searchRequestCountryId"
        >
            <template v-slot:breadcrumbs>
                <hotel-refresh-offers-dialog
                    ref="refreshOffersDialog"
                    :expired-offer-keys="offerKeys"
                    @refresh="onOffersRefreshed"
                />
                <hotel-breadcrumbs :search-request="searchRequest" :offer="offer" />
                <refresh-offers-citizenship-snackbar :basket-item="basketItem" @refresh="refreshExpiredOffers" />
                <page-title :text="$t('page_titles.hotel_booking_page_title')" />
            </template>
            <template
                v-slot:touristsCard="{
                    tourists,
                    bookingKey,
                    basketKey,
                    clearBookingKey,
                    extraServices,
                    isSavingWithoutDetails,
                    formRefs,
                }"
            >
                <template v-for="(roomTourists, roomIndex) in tourists" :key="`tourist-${roomIndex}`">
                    <v-card :data-cy="`room-${roomIndex}`" variant="outlined" class="touristsCard mb-5">
                        <v-card-title class="text-capitalize">
                            <v-icon start color="primary">mdi-account</v-icon>
                            <template v-if="tourists.length > 1">
                                {{ `${$t('room', 1)} ${roomIndex + 1}` }}
                            </template>
                            <template v-else>
                                {{ $t('guests') }}
                            </template>
                        </v-card-title>
                        <v-divider />
                        <v-card-text>
                            <template v-for="(tourist, index) in roomTourists" :key="roomIndex + '_' + index">
                                <hotel-tourist-form
                                    :ref="el => formRefs.push(el)"
                                    v-model="roomTourists[index]"
                                    :room-index="roomIndex"
                                    :index="index"
                                    :disabled="!!bookingKey || !!basketKey"
                                    :show-meal-types="ownProduct"
                                    :standard-meal-name="getMealName(roomIndex)"
                                    :corporate-policy-violation="roomOffers[roomIndex].corporatePolicyViolation"
                                    :hide-index="roomTourists.length === 1"
                                    :start-date="startDate"
                                    :hotel="offer"
                                    :class="{'mb-8': index !== roomTourists.length - 1}"
                                    :data-cy="`tourist-${index}`"
                                    :disable-required-rule="isSavingWithoutDetails"
                                />
                            </template>
                            <early-check-in-late-check-out
                                :disabled="!!bookingKey || !!basketKey"
                                :early-check-in-items="earlyCheckInItems"
                                :late-check-out-items="lateCheckOutItems"
                                :check-in-extra-service-selected="getCheckInExtraServiceSelected(roomIndex)"
                                :check-out-extra-service-selected="getCheckOutExtraServiceSelected(roomIndex)"
                                :check-in-from="checkInFrom"
                                :check-in-to="checkInTo"
                                :check-out-from="checkOutFrom"
                                :check-out-to="checkOutTo"
                                @update-early-check-in-out="updateEarlyCheckInOut($event, roomIndex)"
                            />
                        </v-card-text>
                    </v-card>
                    <extra-services-form
                        v-if="
                            showExtraServices &&
                            productStore.bookingAdditionalOptions[roomIndex] &&
                            productStore.bookingAdditionalOptions[roomIndex].extraServices &&
                            productStore.bookingAdditionalOptions[roomIndex].extraServices.length
                        "
                        v-model="extraServices[roomIndex]"
                        :additional-options="productStore.bookingAdditionalOptions[roomIndex]"
                        :disabled="!!bookingKey || !!basketKey"
                        class="mb-5"
                        @update="clearBookingKey"
                    />
                    <hotel-comments-form
                        :ref="el => formRefs.push(el)"
                        :index="roomIndex"
                        :disabled="!!bookingKey || !!basketKey"
                        :product-store="productStore"
                    />
                </template>
            </template>
            <template
                v-slot:orderSummary="{
                    bookingKey,
                    basketKey,
                    selectedAddOns,
                    extraServices,
                    tourists,
                    selectedMealTypes,
                }"
            >
                <hotel-order-summary
                    :offer="offer"
                    :room-offers="roomOffers"
                    :search-request="searchRequest"
                    :prebooked="!!bookingKey || !!basketKey"
                    :selected-add-ons="selectedAddOns"
                    :extra-services="extraServices"
                    :additional-options="productStore.bookingAdditionalOptions"
                    :tourists="tourists"
                    :selected-meal-types="selectedMealTypes"
                >
                    <template v-slot:summary>
                        <v-card flat rounded="0" class="mb-2 pa-0">
                            <v-card-text class="pa-0">
                                <search-summary-content
                                    :search-request="searchRequest"
                                    tourists-label="guest"
                                    show-dates
                                    show-nights
                                    show-tourists
                                />
                            </v-card-text>
                        </v-card>
                    </template>
                </hotel-order-summary>
            </template>
            <template v-slot:violatedPolicies="{corporatePolicyViolated, violatedPolicies}">
                <corporate-policy-violation v-if="corporatePolicyViolated" :policies="violatedPolicies" />
            </template>
            <conditions-modal>
                <template v-slot:info>
                    <transfer-conditions-modal-info />
                    <activity-conditions-modal-info />
                    <hotel-conditions-modal-info />
                </template>
                <template v-slot:hotelPriceNote>
                    <hotel-price-note :search-request="null" />
                </template>
            </conditions-modal>
            <hotel-meal-type-info-modal />
            <hotel-room-info-modal />
            <additional-fees-modal />
            <transfer-info-modal />
            <activity-info-modal />
        </hotel-booking-page-layout>
        <v-layout v-else class="align-center justify-center">
            <v-progress-circular indeterminate size="64" color="primary" />
        </v-layout>
    </v-layout>
</template>

<script lang="ts">
    import {Component, mixins, Provide, Setup, Watch} from 'vue-facing-decorator'
    import HotelBookingPageLayout from '~src/components/hotels/booking/hotelBookingPageLayout.src'
    import HotelTouristForm from '~src/components/booking/forms/hotelTouristForm.src'
    import HotelCommentsForm from '@/components/booking/forms/HotelCommentsForm.vue'
    import HotelOrderSummary from '~src/components/booking/hotelOrderSummary.src'
    import {HotelPageMixin} from '@/components/hotels/mixins/HotelPageMixin.vue'
    import HotelBreadcrumbs from '@/components/hotels/HotelBreadcrumbs.vue'
    import EarlyCheckInLateCheckOut from '~/components/booking/forms/fields/EarlyCheckInLateCheckOut.vue'
    import HotelRefreshOffersDialog from '@/components/hotels/modals/HotelRefreshOffersDialog.vue'
    import RefreshOffersCitizenshipSnackbar from '@/components/search/RefreshOffersCitizenshipSnackbar.vue'
    import SearchSummaryContent from '@/components/parts/SearchSummaryContent.vue'
    import ExtraServicesForm from '~src/components/booking/forms/ExtraServicesForm.vue'
    import CorporatePolicyViolation from '@/components/snippets/CorporatePolicyViolation.vue'
    import ConditionsModal from '@/components/modals/ConditionsModal.vue'
    import TransferConditionsModalInfo from '@/components/transfers/snippets/TransferConditionsModalInfo.vue'
    import HotelPriceNote from '@/components/hotels/snippets/HotelPriceNote.vue'
    import HotelConditionsModalInfo from '@/components/hotels/snippets/HotelConditionsModalInfo.vue'
    import ActivityConditionsModalInfo from '@/components/activities/snippets/ActivityConditionsModalInfo.vue'
    import HotelMealTypeInfoModal from '@/components/hotels/modals/HotelMealTypeInfoModal.vue'
    import AdditionalFeesModal from '~src/components/modals/AdditionalFeesModal.vue'
    import HotelRoomInfoModal from '@/components/modals/HotelRoomInfoModal.vue'
    import TransferInfoModal from '@/components/transfers/info/TransferInfoModal.vue'
    import ActivityInfoModal from '@/components/activities/info/ActivityInfoModal.vue'
    import PageTitle from '~/components/parts/PageTitle.vue'

    @Component({
        components: {
            ActivityInfoModal,
            TransferInfoModal,
            HotelRoomInfoModal,
            AdditionalFeesModal,
            HotelMealTypeInfoModal,
            ActivityConditionsModalInfo,
            HotelConditionsModalInfo,
            HotelPriceNote,
            TransferConditionsModalInfo,
            ConditionsModal,
            CorporatePolicyViolation,
            ExtraServicesForm,
            RefreshOffersCitizenshipSnackbar,
            HotelBreadcrumbs,
            HotelOrderSummary,
            HotelCommentsForm,
            HotelTouristForm,
            HotelBookingPageLayout,
            EarlyCheckInLateCheckOut,
            HotelRefreshOffersDialog,
            SearchSummaryContent,
            PageTitle,
        },
    })
    export class HotelBookingPage extends mixins(HotelPageMixin) {
        @Setup(() => useOfferExpired()) showOfferExpired: Function

        @Watch('$route.query')
        async onQueryChange() {
            if (this.$route.name !== 'hotelBooking') return
            this.loaded = false
            const promises = []
            promises.push(this.loadTouristBookingFields())
            promises.push(hotelsStore.loadBookingAdditionalOptions(this.offerKeys))
            await Promise.all(promises)
            this.loaded = true
        }

        loaded = false

        async mounted() {
            EventBus.$on('OFFER_EXPIRED_EVENT', () => this.showOfferExpired(this.refreshExpiredOffers))
            await this.$store.restored
            const promises = []
            promises.push(this.loadRuntimeData())
            promises.push(this.loadTouristBookingFields())
            hotelsStore.clearBookingAdditionalOptions()
            promises.push(hotelsStore.loadBookingAdditionalOptions(this.offerKeys))
            await Promise.all(promises)
            await hotelsRuntimeStore.loadOwnProductPaxes()
            this.loaded = true
        }

        unmounted() {
            EventBus.$off('OFFER_EXPIRED_EVENT')
        }

        async loadTouristBookingFields() {
            await runtimeStore.loadTouristBookingFields({offerKey: this.offerKeys[0]})
        }

        //TODO Deprecated
        getMealName(index) {
            return this.roomOffers.length && this.roomOffers[index] ? this.roomOffers[index].mealTypeName : null
        }

        async refreshExpiredOffers(hotelId?: number) {
            hotelsStore.RESET_SEARCH()
            await this.$refs.refreshOffersDialog.search(hotelId)
        }

        onOffersRefreshed() {
            this.$refs.layout.pageLock = false
        }

        updateEarlyCheckInOut({value, type}, roomIndex) {
            const data =
                type === 'earlyCheckIn'
                    ? this.earlyCheckInItems.find(el => el.time === value)
                    : this.lateCheckOutItems.find(el => el.time === value)
            if (!data) {
                hotelsStore.DELETE_CHECK_IN_OUT_EXTRA_SERVICE({roomIndex: roomIndex, type})
                return
            }
            hotelsStore.SET_CHECK_IN_OUT_EXTRA_SERVICE({
                type,
                ...data,
                roomIndex: roomIndex,
            })
        }

        getCheckInOutSelectOptions(optionKey) {
            if (hotelsStore.bookingAdditionalOptions[0]?.[optionKey]) {
                return hotelsStore.bookingAdditionalOptions[0][optionKey].map(opt => {
                    return {
                        ...opt,
                        name: `${opt.time} (+${this.$filters.price(opt.price)})`,
                    }
                })
            }
            return null
        }

        getCheckInExtraServiceSelected(roomIndex) {
            return hotelsStore.checkInOutExtraService[roomIndex]?.earlyCheckIn?.time
        }

        getCheckOutExtraServiceSelected(roomIndex) {
            return hotelsStore.checkInOutExtraService[roomIndex]?.lateCheckOut?.time
        }

        get earlyCheckInItems() {
            return this.getCheckInOutSelectOptions('earlyCheckIn')
        }

        get lateCheckOutItems() {
            return this.getCheckInOutSelectOptions('lateCheckOut')
        }

        get checkInFrom() {
            return hotelsRuntimeStore.hotelInfoResponse?.hotelDescriptionInfo.checkInFrom
        }

        get checkInTo() {
            return hotelsRuntimeStore.hotelInfoResponse?.hotelDescriptionInfo.checkInTo
        }

        get checkOutFrom() {
            return hotelsRuntimeStore.hotelInfoResponse?.hotelDescriptionInfo.checkOutFrom
        }

        get checkOutTo() {
            return hotelsRuntimeStore.hotelInfoResponse?.hotelDescriptionInfo.checkOutTo
        }

        get searchRequestCountryId() {
            return hotelsRuntimeStore.city.countryId
        }

        get offerKeys() {
            // TODO [P3] Workaround for page navigation
            const offerKey = this.$route.query.offerKey || useRoute().query.offerKey
            return !Array.isArray(offerKey) && offerKey ? [offerKey] : offerKey
        }

        get basketItem() {
            return hotelsStore.basketItem(this.offerKeys)
        }

        get offer() {
            return this.basketItem.offer
        }

        get roomOffers() {
            return this.basketItem.roomOffers
        }

        @Provide get searchRequest() {
            return this.basketItem.searchRequest
        }

        get ownProduct() {
            return hotelsRuntimeStore.ownProduct(this.offer)
        }

        get hotelInfoRequest() {
            const {supplierCode, cityCode, hotelCode, hotelId} = this.offer
            return {supplierCode, cityCode, hotelCode, hotelId}
        }

        get cityId() {
            return this.searchRequest.cityId
        }

        get productStore() {
            return hotelsStore
        }

        get showExtraServices() {
            return true
        }

        get startDate() {
            return this.searchRequest.startDate
        }
    }

    export default HotelBookingPage
</script>
